import { useState, useEffect, useRef, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { PanelRightOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";

import {
  AOI_CAMERA,
  DEFAULT_LAYER_STATES,
} from "./config/deforestation";
import {
  LeftPanel,
  GroupPanel,
  LegendPanel,
  ParcelDetailPanel,
  FeatureInfoPopup,
  type FeatureHit,
} from "./components";
import { registerDeforestationLayers } from "./map/register-layers";
import { applyLayerState } from "./map/apply-layer-state";
import {
  CONTEXT_FILL_LAYER_IDS,
  type ContextFillLayerId,
} from "./map/context-layer-meta";
import { buildParcelInfo } from "./map/enrich-parcel";
import { ensureParcelStats, getParcelStats } from "./utils/parcel-stats";
import {
  ensurePortfolioPids,
  getPortfolioPids,
} from "./utils/portfolio";
import {
  getMaptilerStyleUrl,
  getPlaceholderStyle,
  MAPTILER_STYLE_KEYS,
} from "@/components/basemap/styles";
import { useTheme } from "@/components/theme-provider";
import type {
  DeforestationLayerStates,
  Group,
  ParcelInfo,
  SearchMode,
} from "./types/deforestation";

const pmtilesProtocol = new Protocol();
maplibregl.addProtocol("pmtiles", pmtilesProtocol.tile);

function extendBoundsFromGeometry(
  bounds: maplibregl.LngLatBounds,
  geometry: GeoJSON.Geometry,
): void {
  if (geometry.type === "Polygon") {
    for (const ring of geometry.coordinates) {
      for (const c of ring) bounds.extend(c as [number, number]);
    }
  } else if (geometry.type === "MultiPolygon") {
    for (const poly of geometry.coordinates) {
      for (const ring of poly) {
        for (const c of ring) bounds.extend(c as [number, number]);
      }
    }
  }
}

function fitBoundsToFeatures(
  m: maplibregl.Map,
  features: maplibregl.GeoJSONFeature[],
  padding: { top: number; bottom: number; left: number; right: number },
): void {
  if (features.length === 0) return;
  const bounds = new maplibregl.LngLatBounds();
  for (const f of features) {
    extendBoundsFromGeometry(bounds, f.geometry);
  }
  if (!bounds.isEmpty()) {
    m.fitBounds(bounds, { padding, duration: 800, essential: true, maxZoom: 16 });
  }
}

interface VegetationChangePageProps {
  // Per-demo layer relabelling. The NAB-branded demo card passes
  // { portfolio: "NAB Portfolio" }; the generic demo passes nothing and
  // uses the labels from LAYER_DISPLAY_CONFIG.
  layerLabels?: Partial<Record<keyof DeforestationLayerStates, string>>;
}

export function VegetationChangePage({
  layerLabels,
}: VegetationChangePageProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const styleId = isDark
    ? MAPTILER_STYLE_KEYS.DARK
    : MAPTILER_STYLE_KEYS.LIGHT;
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const { enqueueInfoSnackbar } = useCustomSnackbars();

  const [groups, setGroups] = useState<Group[]>([
    { id: "default", name: "Group 1", parcelIds: [] },
  ]);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(
    "default",
  );
  const activeGroupIdRef = useRef<string | null>("default");
 
  const groupsRef = useRef<Group[]>(groups);
  const nextGroupNum = useRef(2);

  const [focusedParcel, setFocusedParcel] = useState<string | null>(null);

  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [featurePopup, setFeaturePopup] = useState<{
    position: { x: number; y: number };
    hits: FeatureHit[];
  } | null>(null);
  const [layers, setLayers] =
    useState<DeforestationLayerStates>(DEFAULT_LAYER_STATES);

  const layersRef = useRef<DeforestationLayerStates>(DEFAULT_LAYER_STATES);
  const [searchMode, setSearchMode] = useState<SearchMode>("parcel");
  const [searchQuery, setSearchQuery] = useState("");
  const [parcelData, setParcelData] = useState<Map<string, ParcelInfo>>(
    () => new Map(),
  );

  const parcelGeomRef = useRef<Map<string, GeoJSON.Geometry>>(new Map());
  const [mapReady, setMapReady] = useState(false);

 
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [portfolioPidsLoaded, setPortfolioPidsLoaded] = useState(false);
  useEffect(() => {
    void ensureParcelStats().then(() => {
      setStatsLoaded(true);
      setParcelData((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        for (const [pid, info] of prev) {
          if (info.stats) continue;
          const s = getParcelStats(pid);
          if (s) next.set(pid, { ...info, stats: s });
        }
        return next;
      });
    });

    void ensurePortfolioPids().then(() => setPortfolioPidsLoaded(true));
  }, []);

  useEffect(() => {
    activeGroupIdRef.current = activeGroupId;
  }, [activeGroupId]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  const allParcelsList = Array.from(parcelData.values());
  const activeGroup =
    groups.find((p) => p.id === activeGroupId) ?? null;
  const activeGroupParcelsList = activeGroup
    ? allParcelsList.filter((p) =>
        activeGroup.parcelIds.includes(p.cadastrePid),
      )
    : [];

  const upsertParcelInfo = useCallback((info: ParcelInfo) => {
    setParcelData((prev) => {
      if (prev.has(info.cadastrePid)) return prev;
      const next = new Map(prev);
      next.set(info.cadastrePid, info);
      return next;
    });
  }, []);

  const handleAddParcelToGroup = useCallback(
    (cadastrePid: string) => {
  
      const current = groupsRef.current;
      if (current.some((p) => p.parcelIds.includes(cadastrePid))) return;

      const currentActiveId = activeGroupIdRef.current;
      const existingTargetId =
        currentActiveId && current.find((p) => p.id === currentActiveId)
          ? currentActiveId
          : current.length > 0
            ? current[current.length - 1].id
            : null;

      const newGroup: Group | null = existingTargetId
        ? null
        : {
            id: crypto.randomUUID(),
            name: `Group ${nextGroupNum.current++}`,
            parcelIds: [],
          };
      const targetId = existingTargetId ?? newGroup?.id;
      if (!targetId) return;

    
      setRightPanelOpen(true);
      setGroups((prev) => {
        const base = newGroup ? [...prev, newGroup] : prev;
        return base.map((p) =>
          p.id === targetId
            ? { ...p, parcelIds: [...p.parcelIds, cadastrePid] }
            : p,
        );
      });
      if (newGroup) setActiveGroupId(targetId);
    },
    [],
  );

  useEffect(() => {
    if (!mapContainerRef.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getPlaceholderStyle(isDarkRef.current),
      center: AOI_CAMERA.center,
      zoom: AOI_CAMERA.zoom,
    });

    map.current.on("style.load", () => {
      const m = map.current;
      if (!m) return;
      registerDeforestationLayers(m);
      applyLayerState(m, layersRef.current);

      const portfolioIds = getPortfolioPids();
      if (portfolioIds.length > 0 && m.getLayer("portfolio-line")) {
        m.setFilter("portfolio-line", [
          "in",
          "cadastrePid",
          ...portfolioIds,
        ]);
      }
      setMapReady(true);
    });

    map.current.on("click", (e) => {
      const m = map.current;
      if (!m) return;

    
      const altPressed =
        e.originalEvent.altKey || e.originalEvent.metaKey;
      const cadastreVisible =
        !!m.getLayer("geoscape-cadastre-fill") &&
        m.getLayoutProperty("geoscape-cadastre-fill", "visibility") !== "none";

      if (cadastreVisible && !altPressed) {
        const cadastreHits = m.queryRenderedFeatures(e.point, {
          layers: ["geoscape-cadastre-fill"],
        });
        const feature = cadastreHits[0];
        if (feature) {
          const info = buildParcelInfo(feature);
          if (info) {
            parcelGeomRef.current.set(info.cadastrePid, feature.geometry);
            upsertParcelInfo(info);
            handleAddParcelToGroup(info.cadastrePid);
          }
          return;
        }
      }

      const visibleContextLayers = CONTEXT_FILL_LAYER_IDS.filter((id) => {
        if (!m.getLayer(id)) return false;
        return m.getLayoutProperty(id, "visibility") !== "none";
      });

      const contextHits = visibleContextLayers.length
        ? m.queryRenderedFeatures(e.point, { layers: visibleContextLayers })
        : [];

      const seenLayers = new Set<string>();
      const hits: FeatureHit[] = [];
      for (const f of contextHits) {
        if (!f.layer || seenLayers.has(f.layer.id)) continue;
        seenLayers.add(f.layer.id);
        hits.push({
          fillLayerId: f.layer.id as ContextFillLayerId,
          feature: f as GeoJSON.Feature,
        });
      }

      if (hits.length > 0) {
        setFeaturePopup({ position: e.point, hits });
      }
    });


    const handleMoveStart = () => setFeaturePopup(null);
    map.current.on("movestart", handleMoveStart);

    const cursorLayers = ["geoscape-cadastre-fill", ...CONTEXT_FILL_LAYER_IDS];
    for (const layerId of cursorLayers) {
      map.current.on("mouseenter", layerId, () => {
        if (map.current) map.current.getCanvas().style.cursor = "pointer";
      });
      map.current.on("mouseleave", layerId, () => {
        if (map.current) map.current.getCanvas().style.cursor = "";
      });
    }

    if (mapContainerRef.current) {
      resizeObserver.current = new ResizeObserver(() => {
        requestAnimationFrame(() => map.current?.resize());
      });
      resizeObserver.current.observe(mapContainerRef.current);
    }

    return () => {
      resizeObserver.current?.disconnect();
      if (map.current) {
        map.current.off("movestart", handleMoveStart);
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;

    setMapReady(false);
    m.setStyle(getMaptilerStyleUrl(styleId));
  }, [styleId]);

  useEffect(() => {
    layersRef.current = layers;
    const m = map.current;
    if (!m || !mapReady) return;
    applyLayerState(m, layers);
  }, [layers, mapReady]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady || !portfolioPidsLoaded) return;
    if (!m.getLayer("portfolio-line")) return;
    const ids = getPortfolioPids();
    m.setFilter(
      "portfolio-line",
      ids.length > 0
        ? ["in", "cadastrePid", ...ids]
        : ["in", "cadastrePid", ""],
    );
  }, [mapReady, portfolioPidsLoaded]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    if (!m.getLayer("geoscape-cadastre-selected")) return;
    const ids = activeGroup?.parcelIds ?? [];
    if (ids.length > 0) {
      m.setFilter("geoscape-cadastre-selected", ["in", "cadastrePid", ...ids]);
    } else {
      m.setFilter("geoscape-cadastre-selected", ["in", "cadastrePid", ""]);
    }
  }, [activeGroup, mapReady]);

  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    if (!m.getLayer("geoscape-cadastre-highlight")) return;
    if (focusedParcel) {
      m.setFilter("geoscape-cadastre-highlight", [
        "==",
        "cadastrePid",
        focusedParcel,
      ]);
      m.setPaintProperty("geoscape-cadastre-highlight", "line-opacity", 1);
    } else {
      m.setFilter("geoscape-cadastre-highlight", ["==", "cadastrePid", ""]);
      m.setPaintProperty("geoscape-cadastre-highlight", "line-opacity", 0);
    }
  }, [focusedParcel, mapReady]);

  const handleRemoveParcel = useCallback(
    (groupId: string, cadastrePid: string) => {
      setGroups((prev) =>
        prev.map((p) =>
          p.id === groupId
            ? {
                ...p,
                parcelIds: p.parcelIds.filter((id) => id !== cadastrePid),
              }
            : p,
        ),
      );
      setFocusedParcel((prev) => (prev === cadastrePid ? null : prev));
    },
    [],
  );

  const handleAddGroup = useCallback(() => {
    const id = crypto.randomUUID();
    const name = `Group ${nextGroupNum.current++}`;
    setGroups((prev) => [...prev, { id, name, parcelIds: [] }]);
    setActiveGroupId(id);
  }, []);

  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      setGroups((prev) => prev.filter((p) => p.id !== groupId));
      if (activeGroupId === groupId) {
        setActiveGroupId((prev) => (prev === groupId ? null : prev));
      }
      setFocusedParcel(null);
    },
    [activeGroupId],
  );

  const handleRenameGroup = useCallback(
    (groupId: string, name: string) => {
      setGroups((prev) =>
        prev.map((p) => (p.id === groupId ? { ...p, name } : p)),
      );
    },
    [],
  );

  const fitBoundsForCadastrePids = useCallback(
    (cadastrePids: string[], padding = 80) => {
      const m = map.current;
      if (!m || cadastrePids.length === 0) return;
      const bounds = new maplibregl.LngLatBounds();
      const missing: string[] = [];
      for (const pid of cadastrePids) {
        const cached = parcelGeomRef.current.get(pid);
        if (cached) {
          extendBoundsFromGeometry(bounds, cached);
        } else {
          missing.push(pid);
        }
      }
      if (missing.length > 0) {
        const features = m.querySourceFeatures("geoscape-cadastre", {
          sourceLayer: "cadastre",
          filter: ["in", "cadastrePid", ...missing],
        });
        for (const f of features) {
          parcelGeomRef.current.set(
            f.properties?.cadastrePid as string,
            f.geometry,
          );
          extendBoundsFromGeometry(bounds, f.geometry);
        }
      }
      if (bounds.isEmpty()) return;
      m.fitBounds(bounds, {
        padding: {
          top: padding,
          bottom: padding,
          left: 340 + padding,
          right: 380 + padding,
        },
        duration: 800,
        essential: true,
        maxZoom: 16,
      });
    },
    [],
  );

  const handleSelectGroup = useCallback(
    (groupId: string) => {
      setActiveGroupId(groupId);
      setFocusedParcel(null);
      setRightPanelOpen(true);
      const group = groups.find((p) => p.id === groupId);
      if (group && group.parcelIds.length > 0) {
        fitBoundsForCadastrePids(group.parcelIds);
      }
    },
    [groups, fitBoundsForCadastrePids],
  );

  const handleFocusParcel = useCallback(
    (cadastrePid: string) => {
      setFocusedParcel(cadastrePid);
      setRightPanelOpen(true);
      fitBoundsForCadastrePids([cadastrePid], 150);
    },
    [fitBoundsForCadastrePids],
  );

  const handleToggleLayer = useCallback(
    (key: keyof DeforestationLayerStates) => {
      setLayers((prev) => ({
        ...prev,
        [key]: { ...prev[key], visible: !prev[key].visible },
      }));
    },
    [],
  );

  const handleSetLayerOpacity = useCallback(
    (key: keyof DeforestationLayerStates, opacity: number) => {
      setLayers((prev) => ({
        ...prev,
        [key]: { ...prev[key], opacity },
      }));
    },
    [],
  );

  const handleSetLayerColor = useCallback(
    (key: keyof DeforestationLayerStates, color: string) => {
      setLayers((prev) => ({
        ...prev,
        [key]: { ...prev[key], color },
      }));
    },
    [],
  );

  const focusedParcelData = focusedParcel
    ? (parcelData.get(focusedParcel) ?? null)
    : null;

  const handleGroupExport = useCallback(() => {
    if (!activeGroup || activeGroupParcelsList.length === 0) return;
    const parcels = activeGroupParcelsList;
    const name = activeGroup.name;
    
    void import("./utils/pdf-export").then(({ generateGroupPDF }) => {
      generateGroupPDF(parcels, name, new Date().toLocaleDateString("en-AU"));
      enqueueInfoSnackbar(
        `Exported ${name} (${parcels.length} parcel${parcels.length !== 1 ? "s" : ""})`,
        "success",
      );
    });
  }, [activeGroup, activeGroupParcelsList, enqueueInfoSnackbar]);

  const handleParcelExport = useCallback(() => {
    if (!focusedParcelData) return;
    const parcel = focusedParcelData;
    const stats = statsLoaded ? getParcelStats(parcel.cadastrePid) : null;
    void import("./utils/pdf-export").then(({ generateParcelPDF }) => {
      generateParcelPDF(parcel, stats, new Date().toLocaleDateString("en-AU"));
      enqueueInfoSnackbar(
        `Exported parcel ${parcel.parcel_id}`,
        "success",
      );
    });
  }, [focusedParcelData, statsLoaded, enqueueInfoSnackbar]);

  return (
    <div
      data-testid="vegetation-change-demo"
      className="absolute inset-0 w-full h-full bg-background overflow-hidden"
    >
      <div
        ref={mapContainerRef}
        className="absolute inset-0 w-full h-full"
        style={{ position: "absolute" }}
      />

      <div className="absolute top-[5rem] group-has-data-[collapsible=icon]/sidebar-wrapper:top-[4rem] left-4 z-20 flex w-[320px] max-h-[calc(100vh-6rem)] group-has-data-[collapsible=icon]/sidebar-wrapper:max-h-[calc(100vh-5rem)] flex-col overflow-hidden rounded-lg border border-border bg-card/90 backdrop-blur shadow-lg">
        <LeftPanel
          searchMode={searchMode}
          searchQuery={searchQuery}
          onSearchModeChange={setSearchMode}
          onSearchQueryChange={setSearchQuery}
          mapRef={map}
          groups={groups}
          activeGroupId={activeGroupId}
          focusedParcel={focusedParcel}
          parcelData={parcelData}
          onSelectGroup={handleSelectGroup}
          onAddGroup={handleAddGroup}
          onDeleteGroup={handleDeleteGroup}
          onRenameGroup={handleRenameGroup}
          onRemoveParcel={handleRemoveParcel}
          onAddParcelToGroup={(
            cadastrePid: string,
            coord?: { lng: number; lat: number },
          ) => {
            const m = map.current;
            if (!m) return;

            // Resolve the parcel against the cadastre tiles and add it.
            // Returns true once the feature is found in a loaded tile.
            const resolve = (): boolean => {
              const features = m.querySourceFeatures("geoscape-cadastre", {
                sourceLayer: "cadastre",
                filter: ["==", "cadastrePid", cadastrePid],
              });
              const feature = features[0];
              if (!feature) return false;
              const info = buildParcelInfo(feature);
              if (!info) return false;
              parcelGeomRef.current.set(info.cadastrePid, feature.geometry);
              upsertParcelInfo(info);
              handleAddParcelToGroup(info.cadastrePid);
              return true;
            };

            // Fast path: parcel is already in a rendered tile.
            if (resolve()) return;

        
            if (!coord) return;
            m.easeTo({
              center: [coord.lng, coord.lat],
              zoom: Math.max(m.getZoom(), 15),
              duration: 800,
            });
            const onIdle = () => {
              if (resolve()) {
                m.off("idle", onIdle);
              }
            };
            m.on("idle", onIdle);
          }}
          onFocusParcel={handleFocusParcel}
          layers={layers}
          onToggleLayer={handleToggleLayer}
          onSetLayerOpacity={handleSetLayerOpacity}
          onSetLayerColor={handleSetLayerColor}
          layerLabels={layerLabels}
        />
        <LegendPanel layers={layers} />
      </div>

      {rightPanelOpen ? (
        <div className="absolute top-[5rem] group-has-data-[collapsible=icon]/sidebar-wrapper:top-[4rem] right-4 z-20 w-sm max-h-[calc(100vh-6rem)] group-has-data-[collapsible=icon]/sidebar-wrapper:max-h-[calc(100vh-5rem)] overflow-y-auto rounded-lg border border-border bg-card/90 backdrop-blur shadow-lg">
          {focusedParcelData ? (
            <ParcelDetailPanel
              parcel={focusedParcelData}
         
              stats={
                statsLoaded
                  ? getParcelStats(focusedParcelData.cadastrePid)
                  : null
              }
              onExport={handleParcelExport}
              // Closing the parcel detail falls back to the group summary;
              // the panel itself stays open.
              onClose={() => setFocusedParcel(null)}
            />
          ) : (
            <GroupPanel
              parcels={activeGroupParcelsList}
              groupName={activeGroup?.name}
              onExport={handleGroupExport}
              onClose={() => setRightPanelOpen(false)}
              onFocusParcel={handleFocusParcel}
            />
          )}
        </div>
      ) : (
        <Button
          variant="outline"
          size="icon"
          onClick={() => setRightPanelOpen(true)}
          aria-label="Show summary panel"
          className="absolute top-[5rem] group-has-data-[collapsible=icon]/sidebar-wrapper:top-[4rem] right-4 z-20 bg-card/90 backdrop-blur shadow-lg"
        >
          <PanelRightOpen className="h-4 w-4" />
        </Button>
      )}

      {featurePopup && (
        <FeatureInfoPopup
          position={featurePopup.position}
          hits={featurePopup.hits}
          layers={layers}
          onClose={() => setFeaturePopup(null)}
        />
      )}
    </div>
  );
}

export default VegetationChangePage;
